<template>
  <div ref="high-priority-alert-wrapper" class="high-priority-alert-wrapper">
    <div
      v-focus-trap
      class="high-priority-alert-container"
      :class="{
        'high-priority-alert-container-compact': isMobileScreenOrCompactWidget
      }"
    >
      <div
        class="alert-content"
        :style="{ ...getFontSizeForChatInterface('bodyText') }"
      >
        <div class="alert-header">
          <BaseIcon
            name="WarningIcon"
            width="24px"
            height="24px"
            fill="#E22C2C"
          />
          <p
            class="header-text"
            :style="{ ...getFontSizeForChatInterface('title') }"
          >
            {{ getAlertTitle }}
          </p>
        </div>
        <div class="details" v-html="sanitizeFontStyles(getAlertDetails)" />
        <div
          v-if="isAlertButtonEnabled && getAlertButtonLabel?.length"
          class="card-button"
          role="button"
          tabindex="0"
          @click.stop="handleAlertButtonClicked"
          @keydown.enter.stop="handleAlertButtonClicked"
        >
          {{ getAlertButtonLabel }}
        </div>
        <div
          class="close-btn"
          role="button"
          tabindex="0"
          @click.stop="handleCloseClicked"
          @keydown.enter.stop="handleCloseClicked"
        >
          Chat Later
        </div>
        <div
          class="alert-timeleft-container"
          :style="{ ...getFontSizeForChatInterface('smallBodyText') }"
        >
          Chat will be available in {{ timeLeft }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/views/BaseIcon.vue'
import {
  emitEventToParentWindow,
  getDeviceType,
  convertUTCTimestampToLocalTimestamp,
  getEventDataPoints
} from '@/services/utils'
import { eventType } from '@/services/constants'
import fontSizeMixin from '@/mixins/fontSizeMixin'

export default {
  components: {
    BaseIcon
  },
  mixins: [fontSizeMixin],
  data() {
    return {
      timeLeft: '',
      intervalTimerId: ''
    }
  },
  computed: {
    isMobileScreenOrCompactWidget() {
      return (
        getDeviceType() === 'mobile' ||
        !this.$store.state.isChatWidgetFullScreen
      )
    },
    getAlertTitle() {
      return this.$store.state.alerts.highPriorityAlert.title
    },
    getAlertDetails() {
      return this.$store.state.alerts.highPriorityAlert.details
    },
    isAlertButtonEnabled() {
      return this.$store.state.alerts.highPriorityAlert.button?.isEnabled
    },
    getAlertButtonLabel() {
      return this.$store.state.alerts.highPriorityAlert.button?.label
    },
    getAlertButtonURL() {
      return this.$store.state.alerts.highPriorityAlert.button?.url
    },
    getEndAlertTimestampWithLocalTimezone() {
      return convertUTCTimestampToLocalTimestamp(
        this.$store.state.alerts.highPriorityAlert.schedule
          .startEndDateTime[1] -
          this.$store.state.alerts.highPriorityAlert.schedule.utcOffsetInMs
      )
    }
  },
  mounted() {
    this.updateDisplayTimeLeft()
    this.intervalTimerId = setInterval(() => {
      this.updateDisplayTimeLeft()
    }, 1000)
  },
  beforeUnmount() {
    if (this.intervalTimerId) {
      clearInterval(this.intervalTimerId)
    }
  },
  methods: {
    sanitizeFontStyles(html) {
      if (!html) return ''
      if (this.$store.getters.isFontFamilyChatInterfaceOverrideEnabled) {
        html = html.replace(/font-family:[^;"]*;?/gi, '')
      }
      if (this.$store.getters.isFontSizeChatInterfaceOverrideEnabled) {
        html = html.replace(/font-size:[^;"]*;?/gi, '')
      }
      return html
    },
    handleCloseClicked() {
      this.$store.dispatch('minimizeCW')
      emitEventToParentWindow(
        eventType.MINIMIZE_CHAT_WIDGET,
        getEventDataPoints()
      )
    },
    updateDisplayTimeLeft() {
      const timeInMS = this.getEndAlertTimestampWithLocalTimezone - Date.now()
      if (timeInMS < 0) return ''
      const seconds = Math.floor(timeInMS / 1000) % 60
      const minutes = Math.floor(timeInMS / (1000 * 60)) % 60
      const hours = Math.floor(timeInMS / (1000 * 60 * 60)) % 24
      const days = Math.floor(timeInMS / (1000 * 60 * 60 * 24))
      const parts = []
      if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`)
      if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`)
      if (minutes > 0) parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`)
      if (seconds > 0) parts.push(`${seconds} second${seconds > 1 ? 's' : ''}`)
      this.timeLeft = parts.length > 0 ? parts.join(', ') : ''
    },
    handleAlertButtonClicked() {
      window.open(this.getAlertButtonURL, 'noopener', 'noreferrer')
    }
  }
}
</script>

<style lang="scss">
@import '@/styles/css/theme.scss';
@import '@/styles/css/commonStyles.scss';

.high-priority-alert-wrapper {
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 2147483647;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.3);
  .high-priority-alert-container {
    @include border-radius(var(--radius-chat-inner-component));
    background: #fff;
    padding: 16px;
    width: 100%;
    max-width: 606px;
    box-sizing: border-box;
    height: auto;
    box-shadow:
      0px 3.2px 7.2px 0px rgba(0, 0, 0, 0.13),
      0px 0.6px 1.8px 0px rgba(0, 0, 0, 0.4);
    background-color: #fcf2f2;
    .alert-content {
      .alert-header {
        display: flex;
        align-items: center;
        gap: 8px;
        .header-text {
          line-height: 22px;
          font-weight: 500;
        }
      }
      .details {
        margin-top: 16px;
        margin-bottom: 24px;
        margin-left: 32px;
        font-size: inherit;
        font-weight: 400;
        line-height: 18px;
        word-wrap: break-word;
        overflow-wrap: break-word;
        word-break: break-word;
        a {
          text-decoration: underline;
          color: #1e6ff1;
        }
      }
      .card-button {
        border-radius: 4px;
        cursor: pointer;
        padding: 10px 24px 10px 24px;
        font-size: inherit;
        font-weight: 600;
        line-height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #000000;
        color: #ffffff;
        &:focus-visible {
          outline: $tab-focused-outline;
        }
      }
      .close-btn {
        cursor: pointer;
        padding: 10px 24px;
        font-size: inherit;
        font-weight: 600;
        line-height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #1c1f2a;
        border-radius: 4px;
        margin-top: 8px;
        &:focus-visible {
          outline: $tab-focused-outline;
        }
      }
      .alert-timeleft-container {
        margin-left: 32px;
        margin-top: 16px;
        font-weight: 400;
        line-height: 18px;
      }
    }
    &.high-priority-alert-container-compact {
      position: absolute;
      bottom: 0;
      padding: 24px;
    }
  }
}
</style>
